import { Info } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { RadixSelectControl } from "../../../components/ui/radix-select";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import EmbeddedDrawer from "./EmbeddedDrawer";
import { useEmbeddedCellDetails } from "./hooks";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import { useSelector } from "react-redux";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { GridEventListener, GridRowParams, GridToolbarContainer } from "@mui/x-data-grid";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor, setIsLoadingStatus } from "../../../store/helpers/helperReducers";
import { setOpenCellDrawer } from "../../../store/api/devices/reducers";
import { prepareCellKpiDataGrid, prepareNrCellKpiDataGrid } from "../../Configuration/helpers";
import SearchBox from "../../../components/Shared/SearchBox/SearchBox";
import { DIALOGOPENFOR, lteSizeData, nrSizeData } from "../../../globals/globals";
import { basicDialogSelector } from "../../../store/helpers/helperSelectors";
import CellKpiChart from "./CellKpisChart";

const EmbeddedCell = () => {
  const dispatch = useStoreDispatch();
  const {
    selectedRow,
    setSelectedRow,
    rowClicked,
    setRowClicked,
    data,
    isLoading,
    cellKpisColumns,
    healthFilter,
    setHealthFilter,
    nrCellKpisColumns,
    selectedDevice,
    startDate,
    setStartDate,
    setIsLoading,
  } = useEmbeddedCellDetails();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const basicDialogOpenFor = useSelector(basicDialogSelector);

  const getDataFromApi: any = data || [];
  const rowsData = selectedDevice?.nodeType === "SN" ? prepareCellKpiDataGrid(getDataFromApi) : prepareNrCellKpiDataGrid(getDataFromApi);

  const handleRowClicked: GridEventListener<"rowClick"> = async (params: GridRowParams<any>) => {
    if (!permissions?.hasOpCodeGroup("view_events") || params.row.modelName === "migration" || params.row.modelName === "unknown") {
      // This is a migrated device that has not yet connected to SNET
      return;
    }
    dispatch(setIsLoadingStatus({ isLoadingEmbeddedCellDetail: true, isLoadingCellsDetails: true }));
    setRowClicked(true);
    dispatch(setOpenCellDrawer(true));
    setSelectedRow(params.row);
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const DataGridTitle = () => {
    return (
      <GridToolbarContainer className="justify-between">
        <span className="text-sm">{`Showing ${rowsData?.length || 0} results`}</span>
      </GridToolbarContainer>
    );
  };

  const handleSelect = (value: string) => {
    if (value) {
      setIsLoading(true);
      setStartDate(Number(value));
    }
  };

  const timeOptions = [
    { label: "Last 7 days", value: 7 * 24 * 60 * 60 * 1000 }, // 7 days in milliseconds
    { label: "Last 30 minutes", value: 30 * 60 * 1000 }, // 30 minutes in milliseconds
    { label: "Last hour", value: 1 * 60 * 60 * 1000 }, // 1 hour in milliseconds
    { label: "Last 24 hours", value: 24 * 60 * 60 * 1000 }, // 24 hours in milliseconds
  ];
  const healthFilters = [
    { value: "all", label: "ALL" },
    { value: "good", label: "Optimal" },
    { value: "bad", label: "Degraded" },
  ];

  console.log("selectedDevice?.nodeType", selectedDevice?.nodeType)

  return (
    <div className="flex flex-col gap-4">
      {basicDialogOpenFor === DIALOGOPENFOR.CELLKPILTECHART && <CellKpiChart
        kpiData={selectedDevice?.nodeType === "SN" ? lteSizeData : nrSizeData}
      />}
      {rowClicked && selectedRow && <EmbeddedDrawer cellDetails={selectedRow} closedButtonClicked={handleCloseButtonClicked} />}
      <div className="flex items-center gap-2">
        <SearchBox tooltip={"Search by 'Cell Name"} />

        <div className="w-[200px] shrink-0">
          <RadixSelectControl
            value={String(startDate)}
            onValueChange={(val) => handleSelect(val)}
            options={timeOptions.map((option) => ({
              value: String(option.value),
              label: option.label,
            }))}
            placeholder="Time"
          />
        </div>

        <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
          {healthFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setHealthFilter(filter.value)}
              disabled={isLoading}
              className={`px-3 text-sm font-medium border-r last:border-r-0 border-border transition-colors ${
                healthFilter === filter.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent text-muted-foreground hover:bg-accent"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 bg-background text-foreground hover:bg-accent"
          onClick={() => dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.CELLKPILTECHART))}
          title="Legend"
        >
          <Info className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-0">
        <CustomDataGrid
          tableId="embeddedCell"
          rows={isLoading ? [] : rowsData}
          columns={selectedDevice?.nodeType === "SN" ? cellKpisColumns : nrCellKpisColumns}
          rowCount={rowsData?.length}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: any) => row.id}
          slots={{ toolbar: DataGridTitle }}
          paginationMode="client"
          sortingMode="client"
          onRowClick={handleRowClicked}
          sx={{
            "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
            "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
          }}
          fillContainer
        />
      </div>
    </div>
  );
};

export default EmbeddedCell;
